import { useState, useEffect, useCallback } from 'react';
import type { MoveWorkerTeamContext } from '@scale/genai-compass-shared/cards/move_worker_team';
import { useCompassCard } from '@scale/compass-ui/store';
import { ColumnSubType } from '@scale/compass-shared/types';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import _ from 'lodash';
import { WorkerTeamEventType } from '@scale/shared/types/events/WorkerTeamEvent';

const useMoveWorkerTeamCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit, error } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext as MoveWorkerTeamContext;

  // Get columns from previous card's output
  const columns = columnsFromRecord(retrievedOutput?.response?.data) || [];
  const data = retrievedOutput?.response?.data ?? [];

  const [numWorkers, setNumWorkers] = useState(0);

  const setNewWorkerTeamId = (newWorkerTeamId: string) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      newWorkerTeamId,
    };
    updateCardContext(updatedContext);

    updateCardCanSubmit(numWorkers > 0 && !_.isEmpty(newWorkerTeamId));
  };

  const setSelectedColumnIdWithWorkerIds = (selectedColumnIdWithWorkerIds: string) => {
    const columnId = selectedColumnIdWithWorkerIds;

    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      selectedColumnIdWithWorkerIds: columnId,
    };
    updateCardContext(updatedContext);

    if (!columnId) {
      return;
    }

    const workerRows = data.filter((row: any) => row[columnId]);

    setNumWorkers(workerRows.length);
    updateCardCanSubmit(workerRows.length > 0 && !_.isEmpty(context.newWorkerTeamId));
  };

  const setReason = useCallback(
    (reason: string | undefined) => {
      const updatedContext: MoveWorkerTeamContext = {
        ...context,
        reason,
      };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  const setAdditionalReasonContext = (additionalReasonContext: string | undefined) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      additionalReasonContext,
    };
    updateCardContext(updatedContext);
  };

  const setAutopermission = (autopermission: boolean) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      autopermission,
    };
    updateCardContext(updatedContext);
  };

  const setDryRun = (dryRun: boolean) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      dryRun,
    };
    updateCardContext(updatedContext);
  };

  const setRejectAttemptsForMovedAttempters = (rejectAttemptsForMovedAttempters: boolean) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      rejectAttemptsForMovedAttempters,
    };
    updateCardContext(updatedContext);
  };

  const setExcludeViolationsWTContributors = (excludeViolationsWTContributors: boolean) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      excludeViolationsWTContributors,
    };
    updateCardContext(updatedContext);
  };

  const setCapacityReqId = (capacityReqId: string) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      capacityReqId,
    };
    updateCardContext(updatedContext);
  };

  const setEventType = (eventType: WorkerTeamEventType) => {
    if (
      eventType !== WorkerTeamEventType.AddWorker &&
      eventType !== WorkerTeamEventType.RemoveWorker
    ) {
      throw new Error(`Invalid event type ${eventType}. Must be AddWorker or RemoveWorker.`);
    }
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      eventType,
    };
    updateCardContext(updatedContext);
  };

  const setRemoveGenAiPermissions = (removeGenAiPermissions: boolean) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      removeGenAiPermissions,
    };
    updateCardContext(updatedContext);
  };

  const setRemovalDestinationTeam = (removalDestinationTeam: string) => {
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      removalDestinationTeam,
    };
    updateCardContext(updatedContext);
  };

  useEffect(() => {
    // Find default column with worker IDs
    let defaultColumnId = context.selectedColumnIdWithWorkerIds;
    if (!defaultColumnId && columns && Array.isArray(columns)) {
      for (const column of columns) {
        if (column.subType === ColumnSubType.WORKER_ID) {
          defaultColumnId = column.id;
          break;
        }
      }
    }

    // Initialize context with proper default values and column selection in one update
    const updatedContext: MoveWorkerTeamContext = {
      ...context,
      eventType: WorkerTeamEventType.AddWorker,
      dryRun: context.dryRun ?? true,
      autopermission: context.autopermission ?? true,
      rejectAttemptsForMovedAttempters: context.rejectAttemptsForMovedAttempters ?? false,
      excludeViolationsWTContributors: context.excludeViolationsWTContributors ?? true,
      selectedColumnIdWithWorkerIds: defaultColumnId,
    };
    updateCardContext(updatedContext);

    // Update worker count if we have a column selected
    if (defaultColumnId) {
      const workerRows = data.filter((row: any) => row[defaultColumnId!]);
      setNumWorkers(workerRows.length);
      updateCardCanSubmit(workerRows.length > 0 && !_.isEmpty(updatedContext.newWorkerTeamId));
    }
  }, []);

  return {
    newWorkerTeamId: context.newWorkerTeamId,
    setNewWorkerTeamId,
    selectedColumnIdWithWorkerIds: context.selectedColumnIdWithWorkerIds,
    setSelectedColumnIdWithWorkerIds,
    reason: context.reason,
    setReason,
    autopermission: context.autopermission,
    setAutopermission,
    dryRun: context.dryRun,
    setDryRun,
    rejectAttemptsForMovedAttempters: context.rejectAttemptsForMovedAttempters,
    setRejectAttemptsForMovedAttempters,
    excludeViolationsWTContributors: context.excludeViolationsWTContributors,
    setExcludeViolationsWTContributors,
    capacityReqId: context.capacityReqId,
    setCapacityReqId,
    eventType: context.eventType,
    setEventType,
    removeGenAiPermissions: context.removeGenAiPermissions,
    setRemoveGenAiPermissions,
    removalDestinationTeam: context.removalDestinationTeam,
    setRemovalDestinationTeam,
    numWorkers,
    additionalReasonContext: context.additionalReasonContext,
    setAdditionalReasonContext,
    columns,
    hasError: !!error,
  };
};

export default useMoveWorkerTeamCard;
